/**
 * Pure reading of the Spraywall half of a daily climb log: the `spray` map of
 * `gyms/{gymId}/climbLogs/{uid}_{YYYY-MM-DD}` (the route half is `climbs`).
 *
 * Two runtimes need the same answer to "which entries of a day count": the
 * client's Napló (journal) and the `onSprayDayLogWrite` trigger that writes
 * `gymStats.sprayDayStats`. One copy here makes a statistics tile equal the sum
 * of the rows the Napló lists by construction.
 *
 * An entry is accepted only when its id prefix and its own `kind` / `problemId`
 * agree (`project_{pid}`, `send_{pid}`, `repeat_{pid}_{atMs}`): a crafted id
 * cannot borrow another problem's row. Malformed day documents and entries are
 * skipped, never thrown on.
 *
 * Spec: docs/spec/02d-shared-spraywall.md ("Spray day log (sprayDayLog.js)").
 */
import { climbAtMillis } from "./climbLog.js";

const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

const isMap = (v) => Object.prototype.toString.call(v) === "[object Object]";

/** The kind an entry id claims for a problem, or null when the id is not the problem's. */
export function sprayEntryKind(entryId, problemId) {
  if (entryId === `project_${problemId}`) return "project";
  if (entryId === `send_${problemId}`) return "send";
  if (entryId.startsWith(`repeat_${problemId}_`)) return "repeat";
  return null;
}

function cleanName(value) {
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * Every valid entry of every day as one flat list (unsorted).
 * `problemName` is a non-empty string or null (a private problem stores no name).
 */
export function sprayDayLogEntries(dayLogs) {
  const out = [];
  for (const dayLog of Array.isArray(dayLogs) ? dayLogs : []) {
    if (!isMap(dayLog) || typeof dayLog.date !== "string" || !DAY_KEY_RE.test(dayLog.date) || !isMap(dayLog.spray)) continue;
    for (const [entryId, entry] of Object.entries(dayLog.spray)) {
      if (!isMap(entry) || typeof entry.problemId !== "string" || !entry.problemId) continue;
      const kind = sprayEntryKind(entryId, entry.problemId);
      if (!kind || entry.kind !== kind) continue;
      out.push({
        date: dayLog.date,
        entryId,
        kind,
        problemId: entry.problemId,
        problemName: cleanName(entry.problemName),
        wallId: typeof entry.wallId === "string" && entry.wallId ? entry.wallId : null,
        wallName: cleanName(entry.wallName),
        flash: kind === "send" && entry.flash === true,
        attempts: Number.isInteger(entry.attempts) ? entry.attempts : null,
        gradeIdx: Number.isInteger(entry.gradeIdx) ? entry.gradeIdx : null,
        at: climbAtMillis(entry),
      });
    }
  }
  return out;
}

/**
 * `{ sends, repeats, projects }` — the `sprayTotals` field of a day document:
 * its `spray` entries counted by `kind`. Every spray writer stores it — the
 * client's log taps and the spray competition's close sync.
 */
export function sprayDayTotals(spray) {
  const totals = { sends: 0, repeats: 0, projects: 0 };
  if (!isMap(spray)) return totals;
  for (const entry of Object.values(spray)) {
    if (entry?.kind === "send") totals.sends += 1;
    else if (entry?.kind === "repeat") totals.repeats += 1;
    else if (entry?.kind === "project") totals.projects += 1;
  }
  return totals;
}

/** The distinct problem ids of a day's `spray` entries — the `problemIds` field. */
export function sprayDayProblemIds(spray) {
  const ids = [];
  if (!isMap(spray)) return ids;
  for (const entry of Object.values(spray)) {
    const pid = entry?.problemId;
    if (typeof pid === "string" && pid && !ids.includes(pid)) ids.push(pid);
  }
  return ids;
}

/**
 * The per-day summary stored as `gymStats.sprayDayStats.<date>`:
 * `{ s: sends, f: flashes, r: repeats, g: { [gradeIdx]: sends } }`. `s + r` is
 * the day's spray climbing volume (a project is not a climb). Only the entries
 * `sprayDayLogEntries([dayLog])` accepts count: `r` counts the `repeat` ones,
 * the rest only the `send` ones; a send without an integer `gradeIdx` counts in `s` (and `f`) but in no `g`
 * key. A document the validity rule skips gives `{ s: 0, f: 0, r: 0, g: {} }`.
 */
export function sprayDaySummary(dayLog) {
  const summary = { s: 0, f: 0, r: 0, g: {} };
  for (const e of sprayDayLogEntries([dayLog])) {
    if (e.kind === "repeat") {
      summary.r += 1;
      continue;
    }
    if (e.kind !== "send") continue;
    summary.s += 1;
    if (e.flash) summary.f += 1;
    if (e.gradeIdx !== null) summary.g[String(e.gradeIdx)] = (summary.g[String(e.gradeIdx)] || 0) + 1;
  }
  return summary;
}
